import { HeaderPage } from "@/components/HeaderPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table } from "@/components/Table";
import { CardContent } from "@/components/CardContent";
import { Search, Plus, Eye, Trash2, Loader2 } from "lucide-react";
import type { Notification } from "@/types/Notification";
import { MdOutlineEdit } from "react-icons/md";
import { Loading } from "@/components/Loading";
import {
  useGetNotifications,
  useDeleteNotification,
} from "@/hooks/useNotifications";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

export const Notificacoes = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [type, setType] = useState<string>("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState<
    string | null
  >(null);

  const debouncedSearch = useDebounce(search, 500);
  const deleteMutation = useDeleteNotification();

  const { data, isLoading } = useGetNotifications({
    page,
    limit: 10,
    title: debouncedSearch || undefined,
    type: type === "all" ? undefined : type,
  });

  const notifications = data?.data || [];

  const handleDeleteClick = (id: string) => {
    setNotificationToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!notificationToDelete) return;

    try {
      await deleteMutation.mutateAsync(notificationToDelete);
      toast.success("Notificação deletada com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      setNotificationToDelete(null);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao deletar notificação");
    }
  };

  return (
    <>
      <HeaderPage
        title="Notificações"
        description="Crie e gerencie notificações enviadas aos usuários do sistema"
      />

      <Card className="mb-6 mt-6 bg-white">
        <CardContentUI className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar notificações..."
                className="pl-10 h-10 text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex gap-3">
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="w-45 h-10 text-sm">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Tipos</SelectItem>
                  <SelectItem value="GENERAL_ANNOUNCEMENT">Anúncio Geral</SelectItem>
                  <SelectItem value="NEIGHBORHOOD_ANNOUNCEMENT">Anúncio de Bairro</SelectItem>
                  <SelectItem value="OCCURRENCE_UPDATE">Atualização de Ocorrência</SelectItem>
                  <SelectItem value="CITY_HALL_RESPONSE">Resposta da Prefeitura</SelectItem>
                </SelectContent>
              </Select>
              <Button
                className="h-10 text-sm px-4 text-white cursor-pointer bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]"
                onClick={() => navigate("/notifications/create")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova Notificação
              </Button>
            </div>
          </div>
        </CardContentUI>
      </Card>

      <CardContent
        title="Lista de Notificações"
        content={
          isLoading ? (
            <Loading label="Carregando notificações..." />
          ) : (
            <Table<Notification>
              columns={[
                {
                  key: "id",
                  label: "Id",
                },
                {
                  key: "title",
                  label: "Título",
                  render: (notification) => (
                    <div className="max-w-xs truncate">
                      {notification.title}
                    </div>
                  ),
                },
                {
                  key: "message",
                  label: "Mensagem",
                  render: (notification) => (
                    <div className="max-w-md truncate">
                      {notification.message}
                    </div>
                  ),
                },
                {
                  key: "type",
                  label: "Tipo",
                  render: (notification) => {
                    const typeConfig: Record<
                      string,
                      { label: string; className: string }
                    > = {
                      GENERAL_ANNOUNCEMENT: {
                        label: "Anúncio Geral",
                        className:
                          "bg-blue-100 text-blue-800 hover:bg-blue-100",
                      },
                      NEIGHBORHOOD_ANNOUNCEMENT: {
                        label: "Anúncio de Bairro",
                        className:
                          "bg-green-100 text-green-800 hover:bg-green-100",
                      },
                      OCCURRENCE_UPDATE: {
                        label: "Atualização de Ocorrência",
                        className:
                          "bg-purple-100 text-purple-800 hover:bg-purple-100",
                      },
                      CITY_HALL_RESPONSE: {
                        label: "Resposta da Prefeitura",
                        className:
                          "bg-indigo-100 text-indigo-800 hover:bg-indigo-100",
                      },
                      USER_ANNOUNCEMENT: {
                        label: "Usuário",
                        className:
                          "bg-orange-100 text-orange-800 hover:bg-orange-100",
                      },
                      GENERAL: {
                        label: "Geral",
                        className:
                          "bg-blue-100 text-blue-800 hover:bg-blue-100",
                      },
                      NEIGHBORHOOD: {
                        label: "Bairro",
                        className:
                          "bg-green-100 text-green-800 hover:bg-green-100",
                      },
                      USER: {
                        label: "Usuário",
                        className:
                          "bg-orange-100 text-orange-800 hover:bg-orange-100",
                      },
                    };

                    const config = typeConfig[notification.type] || {
                      label: notification.type,
                      className: "bg-gray-100 text-gray-800 hover:bg-gray-100",
                    };

                    return (
                      <Badge className={config.className}>{config.label}</Badge>
                    );
                  },
                },
                {
                  key: "createdAt",
                  label: "Data de Criação",
                  render: (notification) => formatDate(notification.createdAt),
                },
                {
                  key: "actions",
                  label: "Ações",
                  render: (notification) => {
                    return (
                      <div className="flex items-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                          title="Visualizar"
                          onClick={() =>
                            navigate(`/notifications/view/${notification.id}`)
                          }
                        >
                          <Eye className="h-5 w-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer"
                          title="Deletar"
                          onClick={() => handleDeleteClick(notification.id)}
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                    );
                  },
                },
              ]}
              data={notifications}
              pagination={data?.meta}
              onPageChange={setPage}
            />
          )
        }
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Confirmar exclusão"
        description="Tem certeza que deseja deletar esta notificação? Esta ação não pode ser desfeita."
        confirmText="Deletar"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </>
  );
};
